/**
 * <gg-promotion-banner> — floating top-left promotions banner.
 *
 * Top-left counterpart to the top-right cart toast: same floating chrome, but
 * persistent with a live progress bar. Shows the single highest-priority active
 * promotion with a headline that's relevant to the current cart. Renders only once
 * the cart has items (luxury restraint). × dismisses it for the session (per-promo,
 * persisted in sessionStorage across full-page navigation); a met gift shows a claim/
 * choose button that hands off to the cart widget.
 */

import { ThemeEvents } from '@theme/events';
import {
  fetchActivePromotions,
  fetchCart,
  matchesScope,
  lineSubject,
  progressToward,
  mediaHTML,
  barPercent,
  localeText,
  isConsumed,
  showsBar,
  giftActionHTML,
  progressLabelHTML,
  animateProgressFills,
  esc,
  requestClaimInCart,
} from '@theme/promotions-shared';

const STATE_KEY = 'gg_promo_banner_state'; // { [promoId]: 'dismissed' }

class GgPromotionBanner extends HTMLElement {
  #lang = 'de';
  #promo = null;

  connectedCallback() {
    this.#lang = this.dataset.lang || 'de';
    if (this.dataset.giftIcon) window.__ggGiftIcon = this.dataset.giftIcon;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#update();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = () => {
    this.#update();
  };

  async #update() {
    const [promotions, cart] = await Promise.all([
      fetchActivePromotions(),
      fetchCart(),
    ]);

    // Luxury restraint: no banner on an empty cart.
    if (!cart.items?.length) return this.#hide();

    // Highest-priority promo (feed is priority-sorted) that has a headline and is
    // relevant site-wide: scope=any OR at least one cart line matches its scope.
    // NOTE: intentionally NOT gated by showsOn()/display.surfaces — per the contract §8
    // the banner is a v2 surface with no per-promo toggle (PromotionSurface has only
    // cart_drawer/cart_page/pdp). It shows any active promo WITH a headline. If a `banner`
    // surface key is ever added backend-side, add `showsOn(p, 'banner')` to this filter.
    const subjects = cart.items.filter((l) => !l.properties?._gg_gift).map(lineSubject);
    this.#promo = promotions.find((p) => {
      const text = localeText(p, this.#lang);
      if (!text?.headline) return false;
      if (isConsumed(cart, p)) return false; // claimed gift OR maxed tier → skip to next promo
      const scope = p.condition?.scope;
      return scope?.mode === 'any' || subjects.some((s) => matchesScope(s, scope));
    });

    if (!this.#promo) return this.#hide();
    // × dismisses this promo for the session — don't re-show it.
    if (this.#stateFor(this.#promo.id) === 'dismissed') return this.#hide();

    await this.#renderPromo(cart);
    this.#show();
  }

  async #renderPromo(cart) {
    const promo = this.#promo;
    const progress = progressToward(cart, promo);
    const text = localeText(promo, this.#lang) || {};
    const pct = barPercent(cart, promo, progress);
    const media = await mediaHTML(promo, cart);

    // Met gift → claim/choose control, handed off to the cart widget (banner never mutates).
    const action = giftActionHTML(promo, progress);
    // Met fixed gift shows the button in place of the bar (mirrors cart/PDP).
    const showBar = showsBar(promo, progress);

    this.innerHTML = `
      <div class="gg-promo-banner__media">${media}</div>
      <div class="gg-promo-banner__body">
        <div class="promo-title"><span class="promo-dot"></span>${esc(text.headline)}</div>
        ${showBar ? `
          <div class="promo-progress"><div class="promo-progress__fill" style="--promo-pct:${pct}%"></div></div>
          <div class="promo-progress-label">${progressLabelHTML(promo, progress, text, cart)}</div>` : ''}
        ${action}
      </div>
      <button type="button" class="gg-promo-banner__close" aria-label="Schließen">&times;</button>
    `;
    this.#wire();
  }

  #wire() {
    // × dismisses this promo's banner for the session (no peek-tab; reappears next session
    // or when a different higher-priority promo becomes relevant).
    this.querySelector('.gg-promo-banner__close')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#setState(this.#promo.id, 'dismissed');
      this.#hide();
    });
    // Met gift claim/choose → hand off to the cart widget (opens the drawer, claims there).
    this.querySelector('[data-promo-claim]')?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      requestClaimInCart(this.#promo.id, 'claim');
    });
    this.querySelector('[data-promo-pick]')?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      requestClaimInCart(this.#promo.id, 'pick');
    });
  }

  #show() {
    this.hidden = false;
    this.classList.remove('is-leaving'); // cancel any in-progress exit
    requestAnimationFrame(() => this.classList.add('is-visible'));
    animateProgressFills(this);
  }

  #hide() {
    if (this.hidden) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.remove('is-visible', 'is-leaving');
      this.hidden = true;
      return;
    }
    // Dissolve out in place (.is-leaving: fade + gentle zoom, like the toast) BEFORE removing
    // from layout — setting hidden immediately would display:none it and skip the animation.
    this.classList.remove('is-visible');
    this.classList.add('is-leaving');
    const done = (e) => {
      if (e && e.target !== this) return; // ignore descendant transitions
      this.removeEventListener('transitionend', done);
      clearTimeout(fallback);
      if (this.classList.contains('is-leaving')) { // wasn't re-shown mid-exit
        this.hidden = true;
        this.classList.remove('is-leaving');
      }
    };
    this.addEventListener('transitionend', done);
    const fallback = setTimeout(done, 550); // safety if transitionend doesn't fire
  }

  #stateFor(id) {
    return this.#readState()[id];
  }

  #setState(id, value) {
    const state = this.#readState();
    state[id] = value;
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (_) {}
  }

  #readState() {
    try {
      return JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}');
    } catch (_) {
      return {};
    }
  }
}

if (!customElements.get('gg-promotion-banner')) {
  customElements.define('gg-promotion-banner', GgPromotionBanner);
}
